import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from './use-local-storage.ts';
import type { ICartItemSchema, IStoreCartLineItemSchema } from '@r/types/zod-schemas.ts';
import type { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { canFulfillStock, getProductQuantity } from '@r/utils/product-stock.ts';

type CartItem = z.infer<typeof ICartItemSchema>;
type CartOperationResult = {
  success: boolean;
  error?: string;
};

/**
 * Hook to manage shopping cart operations using localStorage
 * Handles multiple carts for different user/workspace combinations
 * @param userId - The current user ID
 * @param workspaceId - The current workspace ID
 */
const useCart = (userId: number | undefined, workspaceId: number | undefined) => {
  const { t } = useTranslation();
  const STORAGE_KEY = 'carts' as const;
  const { getItem, setItem } = useLocalStorage();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  /**
   * Generates a unique key for the current user's cart
   * Format: cart-{userId}-{workspaceId}
   */
  const getCartKey = useCallback((): string => {
    if (!userId || !workspaceId) {
      throw new Error(t('cart.error.missing.ids'));
    }
    return `cart-${userId}-${workspaceId}`;
  }, [userId, workspaceId]);

  /**
   * Initialize cart on component mount or when user/workspace changes
   * Creates an empty cart if none exists for the current user
   */
  useEffect(() => {
    if (!userId || !workspaceId) return;

    const cartKey = getCartKey();
    const cart = getItem(STORAGE_KEY);

    if (!cart) {
      const initialCart = { [cartKey]: [] };
      setItem(STORAGE_KEY, JSON.stringify(initialCart));
      setCartItems([]);
      return;
    }

    const parsedCarts = JSON.parse(cart);
    const currentCart = parsedCarts[cartKey] || [];

    if (currentCart.length > 0 && currentCart.some((item: CartItem) => !('addedAt' in item))) {
      parsedCarts[cartKey] = [];

      setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
      setCartItems([]);

      return;
    }

    if (!parsedCarts[cartKey]) {
      parsedCarts[cartKey] = [];
      setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
    }

    setCartItems(parsedCarts[cartKey]);
  }, [userId, workspaceId, getCartKey, getItem, setItem]);

  /**
   * Calculates cart totals including subtotal, shipping, discounts, etc.
   * @param items - Array of cart items to calculate totals for
   */
  const calculateCartTotals = useCallback((items: CartItem[]) => {
    const totals = {
      total: 0,
      subtotal: 0,
      tax_total: 0,
      shipping_total: 0,
      discount_total: 0,
      gift_card_total: 0,
      currency_code: 'EUR',
      shipping_subtotal: 0
    };

    for (const item of items) {
      const itemPrice = item.product.pointUnitPrice;
      const itemSubtotal =
        itemPrice *
        (item.product.dynamicPrice
          ? (item.dynamicPriceAmount ?? 1) / (item.product.dynamicQuantity ?? 1)
          : item.quantity);
      totals.subtotal += itemSubtotal;
    }

    totals.total =
      totals.subtotal + totals.shipping_total - totals.discount_total - totals.gift_card_total;

    return totals;
  }, []);

  /**
   * Returns cart items with calculated totals
   * Used for displaying cart summary
   */
  const cartWithTotals = useMemo(
    () => ({
      items: cartItems,
      totals: calculateCartTotals(cartItems)
    }),
    [cartItems, calculateCartTotals]
  );

  /**
   * Returns total number of items in cart
   * Used for displaying cart badge count
   */
  const cartItemCount = useMemo(
    () => cartItems.reduce((total, item) => total + (item.quantity || 1), 0),
    [cartItems]
  );

  /**
   * Merges or adds new item to cart
   * Special handling for items with dynamic pricing (always adds as new item)
   * @param currentCart - Current cart items
   * @param newItem - Item to add
   */
  const mergeCartItems = useCallback((currentCart: CartItem[], newItem: CartItem): CartItem[] => {
    if (newItem.dynamicPriceAmount !== undefined) {
      return [...currentCart, newItem];
    }

    const existingItemIndex = currentCart.findIndex(
      (cartItem) =>
        cartItem.iri === newItem.iri &&
        cartItem.dynamicPriceAmount === undefined &&
        (!cartItem.variant?.id ||
          !newItem.variant?.id ||
          cartItem.variant.id === newItem.variant.id)
    );

    if (existingItemIndex === -1) {
      return [...currentCart, newItem];
    }

    const updatedCart = [...currentCart];
    updatedCart[existingItemIndex] = {
      ...updatedCart[existingItemIndex],
      product: newItem.product,
      quantity: updatedCart[existingItemIndex].quantity + newItem.quantity
    };

    return updatedCart;
  }, []);

  /**
   * Adds new item to cart
   * Handles merging with existing items
   */
  const addItemToCart = useCallback(
    async (item: CartItem): Promise<CartOperationResult> => {
      try {
        const allCarts = getItem(STORAGE_KEY);
        const parsedCarts = allCarts ? JSON.parse(allCarts) : {};
        const currentCart: CartItem[] = parsedCarts[getCartKey()] || [];
        const requestedQuantity = getProductQuantity(currentCart, item.product.id) + item.quantity;

        if (!canFulfillStock(item.product.stock, requestedQuantity)) {
          return { success: false, error: t('cart.error.stock.insufficient') };
        }

        const newCart = mergeCartItems(currentCart, item);
        parsedCarts[getCartKey()] = newCart;
        setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
        setCartItems(newCart);
        return { success: true };
      } catch (error) {
        console.error('Failed to add item to cart:', error);
        return {
          success: false,
          error: t('cart.error.add.item')
        };
      }
    },
    [getItem, setItem, getCartKey, mergeCartItems, t]
  );

  /**
   * Updates quantity of specific cart item
   * @param cartItem - item in cart
   * @param quantity - New quantity
   */
  const updateCartItemQuantity = useCallback(
    async ({
      cartItem,
      quantity
    }: {
      cartItem: z.infer<typeof IStoreCartLineItemSchema>;
      quantity: number;
    }): Promise<CartOperationResult> => {
      try {
        const allCarts = getItem(STORAGE_KEY);
        if (!allCarts) return { success: false, error: t('cart.error.not.found') };

        const parsedCarts = JSON.parse(allCarts);
        const currentCart = parsedCarts[getCartKey()];

        if (!currentCart) return { success: false, error: t('cart.error.user.not.found') };

        const itemIndex = currentCart.findIndex((item: CartItem) => {
          const productIdMatch = item.product.id === Number.parseInt(cartItem.id, 10);

          return cartItem.dynamic_price_amount
            ? productIdMatch && item.addedAt === cartItem.added_at
            : productIdMatch;
        });

        if (itemIndex === -1) return { success: false, error: t('cart.error.item.not.found') };

        const item = currentCart[itemIndex] as CartItem;
        const otherLinesQuantity = getProductQuantity(currentCart, item.product.id) - item.quantity;

        if (!canFulfillStock(item.product.stock, otherLinesQuantity + quantity)) {
          return { success: false, error: t('cart.error.stock.insufficient') };
        }

        currentCart[itemIndex].quantity = quantity;
        parsedCarts[getCartKey()] = currentCart;
        setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
        setCartItems(currentCart);
        return { success: true };
      } catch (error) {
        console.error('Failed to update cart item quantity:', error);
        return { success: false, error: t('cart.error.update.quantity') };
      }
    },
    [getItem, setItem, getCartKey, t]
  );

  /**
   * Removes item from cart by index or ID
   * @param id - Product ID to remove
   */
  const removeCartItem = useCallback(
    async ({
      cartItem
    }: {
      cartItem: z.infer<typeof IStoreCartLineItemSchema>;
    }): Promise<CartOperationResult> => {
      try {
        const allCarts = getItem(STORAGE_KEY);
        if (!allCarts) return { success: false, error: t('cart.error.not.found') };

        const parsedCarts = JSON.parse(allCarts);
        const currentCart = parsedCarts[getCartKey()];
        if (!currentCart) return { success: false, error: t('cart.error.user.not.found') };

        const itemIndex = currentCart.findIndex((item: CartItem) => {
          const productIdMatch = item.product.id === Number.parseInt(cartItem.id, 10);

          return cartItem.dynamic_price_amount
            ? productIdMatch && item.addedAt === cartItem.added_at
            : productIdMatch;
        });

        if (itemIndex >= 0) {
          currentCart.splice(itemIndex, 1);
        }

        parsedCarts[getCartKey()] = currentCart;
        setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
        setCartItems(currentCart);

        return { success: true };
      } catch (error) {
        console.error('Failed to remove cart item:', error);
        return { success: false, error: t('cart.error.remove.item') };
      }
    },
    [getItem, setItem, getCartKey]
  );

  /**
   * Clears entire cart for current user
   * Used after order completion
   */
  const clearCart = useCallback(async (): Promise<CartOperationResult> => {
    try {
      const allCarts = getItem(STORAGE_KEY);
      if (!allCarts) return { success: false, error: t('cart.error.not.found') };

      const parsedCarts = JSON.parse(allCarts);
      delete parsedCarts[getCartKey()];

      setItem(STORAGE_KEY, JSON.stringify(parsedCarts));
      setCartItems([]);
      return { success: true };
    } catch (error) {
      console.error(error);
      return { success: false, error: t('cart.error.clear.cart') };
    }
  }, [getItem, setItem, getCartKey]);

  return useMemo(
    () => ({
      cartItems,
      cartWithTotals,
      cartItemCount,
      addItemToCart,
      updateCartItemQuantity,
      removeCartItem,
      clearCart
    }),
    [
      cartItems,
      cartWithTotals,
      cartItemCount,
      addItemToCart,
      updateCartItemQuantity,
      removeCartItem,
      clearCart
    ]
  );
};

export { useCart };
